/**
 * `useProviderCalendar` — the provider calendar data hook.
 *
 * Resolves the logged-in user's `Practitioner`, queries Medplum `Appointment`
 * resources for the selected practitioners + visible window, and maps them
 * into calendar UI events.
 */

import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveCurrentUserPractitioner } from '../../lib/practitionerIdentity';
import { buildPractitionerAppointmentSubscriptionCriteria } from '../../lib/fhirRealtimeCriteria';
import { useMedplumSubscriptionRefresh } from '../../hooks/useMedplumSubscriptionRefresh';
import { getProviderCalendarEventsDetailed } from './getProviderCalendarEvents';
import type { ProviderCalendarEvent, ProviderCalendarWindow } from './types';

export interface UseProviderCalendarOptions {
  /** Visible calendar range (e.g. week/day/month). */
  window: ProviderCalendarWindow;
  /**
   * Selected practitioner calendars to load. When empty/undefined, falls back
   * to the logged-in practitioner only.
   */
  selectedPractitionerIds?: readonly string[];
  /** Display label used for `event.provider` on the viewer's own appointments. */
  currentProviderDisplayLabel?: string;
  /** Owner id → canManage from calendar access resolution. */
  canManageByOwnerId?: ReadonlyMap<string, boolean>;
}

export interface UseProviderCalendarResult {
  events: ProviderCalendarEvent[];
  loading: boolean;
  error: string | null;
  /** Practitioner ids whose actor search failed (partial failure). */
  failedPractitionerIds: string[];
  /** True when we resolved the current Practitioner. */
  hasResolvedPractitioner: boolean;
  practitionerId: string | null;
  /** Re-run the live fetch. */
  refresh: () => void;
}

function windowKey(window: ProviderCalendarWindow): string {
  return `${window.start.toISOString()}|${window.end.toISOString()}`;
}

function idsKey(ids: readonly string[] | undefined): string {
  return (ids ?? []).join(',');
}

export function useProviderCalendar(
  opts: UseProviderCalendarOptions,
): UseProviderCalendarResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const [practitionerId, setPractitionerId] = useState<string | null>(null);
  const [events, setEvents] = useState<ProviderCalendarEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [failedPractitionerIds, setFailedPractitionerIds] = useState<string[]>([]);
  const [reloadTick, setReloadTick] = useState(0);

  const wKey = useMemo(() => windowKey(opts.window), [opts.window]);
  const selectedKey = useMemo(
    () => idsKey(opts.selectedPractitionerIds),
    [opts.selectedPractitionerIds],
  );

  /** Resolve the logged-in Practitioner once per profile. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (profile?.resourceType === 'Practitioner' && profile.id) {
          if (!cancelled) setPractitionerId(profile.id);
          return;
        }
        const pract = await resolveCurrentUserPractitioner(medplum, profile ?? undefined);
        if (!cancelled) setPractitionerId(pract?.id ?? null);
      } catch {
        if (!cancelled) setPractitionerId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, profile]);

  /** Fetch Appointments for selected practitioners + visible window. */
  useEffect(() => {
    if (!practitionerId) {
      return;
    }

    const selected =
      opts.selectedPractitionerIds && opts.selectedPractitionerIds.length > 0
        ? [...opts.selectedPractitionerIds]
        : [practitionerId];

    let cancelled = false;
    setLoading(true);
    setError(null);

    medplum.invalidateSearches('Appointment');
    getProviderCalendarEventsDetailed({
      medplum,
      practitionerIds: selected,
      window: opts.window,
      currentPractitionerId: practitionerId,
      currentProviderDisplayLabel: opts.currentProviderDisplayLabel,
      canManageByOwnerId: opts.canManageByOwnerId,
    })
      .then((result) => {
        if (cancelled) return;
        setEvents(result.events);
        setFailedPractitionerIds(result.failedPractitionerIds);
        if (result.failedPractitionerIds.length > 0 && result.events.length === 0) {
          setError('Failed to load calendar appointments');
        } else if (result.failedPractitionerIds.length > 0) {
          setError('Some practitioner calendars failed to load');
        } else {
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load appointments';
        console.warn('[useProviderCalendar] live fetch failed:', err);
        setEvents([]);
        setFailedPractitionerIds(selected);
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally depend on wKey / selectedKey rather than object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    medplum,
    practitionerId,
    wKey,
    selectedKey,
    reloadTick,
    opts.currentProviderDisplayLabel,
    opts.canManageByOwnerId,
  ]);

  const refresh = useCallback(() => {
    setReloadTick((t) => t + 1);
  }, []);

  /** Live updates when appointments for visible practitioners change (e.g. client portal cancel). */
  const subscriptionActorIds = useMemo(() => {
    if (!practitionerId) return [] as string[];
    const selected =
      opts.selectedPractitionerIds && opts.selectedPractitionerIds.length > 0
        ? [...opts.selectedPractitionerIds]
        : [practitionerId];
    return [...new Set(selected.filter(Boolean))];
  }, [practitionerId, selectedKey, opts.selectedPractitionerIds]);

  // Medplum WS is one criteria per hook; cover the common multi-calendar selection without a dynamic hook loop.
  useMedplumSubscriptionRefresh(
    subscriptionActorIds[0]
      ? buildPractitionerAppointmentSubscriptionCriteria(subscriptionActorIds[0])
      : undefined,
    refresh,
    { debugLabel: '[ProviderCalendar][WS][0]' },
  );
  useMedplumSubscriptionRefresh(
    subscriptionActorIds[1]
      ? buildPractitionerAppointmentSubscriptionCriteria(subscriptionActorIds[1])
      : undefined,
    refresh,
    { debugLabel: '[ProviderCalendar][WS][1]' },
  );
  useMedplumSubscriptionRefresh(
    subscriptionActorIds[2]
      ? buildPractitionerAppointmentSubscriptionCriteria(subscriptionActorIds[2])
      : undefined,
    refresh,
    { debugLabel: '[ProviderCalendar][WS][2]' },
  );
  useMedplumSubscriptionRefresh(
    subscriptionActorIds[3]
      ? buildPractitionerAppointmentSubscriptionCriteria(subscriptionActorIds[3])
      : undefined,
    refresh,
    { debugLabel: '[ProviderCalendar][WS][3]' },
  );

  /** Re-fetch when the browser tab becomes visible again. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  return {
    events,
    loading,
    error,
    failedPractitionerIds,
    hasResolvedPractitioner: practitionerId !== null,
    practitionerId,
    refresh,
  };
}
